import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    signal,
    viewChild
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { map } from "rxjs";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatIconButton } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { HttpErrorResponse } from "@angular/common/http";
import { ButtonComponent, ButtonVariant, InputOutline, TextareaComponent } from "@sxm-content-creators/tools-ds";
import { SnackbarService } from "../../../../components/snackbar/snackbar.service";
import { SubmissionFreeformApiService } from "../../services/submission-freeform.api.service";
import {
    AUDIO_FILE_EXTENSIONS,
    MAX_AUDIO_SIZE_BYTES,
    MAX_AUDIO_SIZE_MB,
    SubmissionAudioMimeType,
    SubmissionFreeformHostResponseRequest,
    SubmissionFreeformResponse
} from "../../models/submission-freeform.model";
import { BadgeResponse } from "../../../../admin/themes/models/theme.model";
import { SubmitterContactResponse } from "../../models/submitter.model";

const RESPONSE_TEXT_MAX_LENGTH = 1000;
// Client mirrors the copy (backend allows more MIME types, but the UI only advertises WAV/MP3, so we only need to accept what we advertise).
const ALLOWED_RESPONSE_AUDIO_FORMATS: readonly { label: string; mimeType: SubmissionAudioMimeType }[] = [
    { label: "WAV", mimeType: "audio/wav" },
    { label: "MP3", mimeType: "audio/mpeg" }
];

export interface ModalData {
    submission: SubmissionFreeformResponse;
    submitter: SubmitterContactResponse | null;
    badges: BadgeResponse[];
    isEditing: boolean;
    // The caller's already-fetched response audio blob URL, if it has one cached, so the modal
    // can skip re-fetching it. The modal never revokes this URL itself (it doesn't own it) -
    // only a caller-provided cache should decide when to free it.
    audioPreviewUrl?: string | null;
}

export interface ResponseModalResult {
    // True if the audio file was uploaded (replacing the S3 object at responseAudioPath's fixed
    // per-submission key, even though the path string itself may not have changed) or removed
    // this session, so callers know whether any cached copy of it needs to be invalidated.
    audioChanged: boolean;
}

@Component({
    selector: "app-response-modal",
    templateUrl: "./response-modal.component.html",
    styleUrl: "./response-modal.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatIconButton,
        ButtonComponent,
        TextareaComponent,
        InputOutline
    ]
})
export class ResponseModalComponent {
    readonly data = inject<ModalData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject(MatDialogRef<ResponseModalComponent, ResponseModalResult>);
    private readonly submissionFreeformApi = inject(SubmissionFreeformApiService);
    private readonly snackbar = inject(SnackbarService);

    readonly badgeOptions = this.data.badges
        .filter(badge => badge.responseIsActive && (badge.imageUrl !== null || !!badge.response))
        .sort((a, b) => a.displayOrder - b.displayOrder);

    readonly badgeControl = new FormControl<string | null>(this.data.submission.responseRating);
    readonly textControl = new FormControl(this.initialText(), {
        nonNullable: true,
        validators: [Validators.maxLength(RESPONSE_TEXT_MAX_LENGTH)]
    });
    readonly linkControl = new FormControl(this.data.submission.responseLink ?? "", { nonNullable: true });
    readonly hideImage = signal(this.data.submission.responseHideBadgeImage);
    readonly isSubmitting = signal(false);

    private readonly audioChanged = signal(false);
    private readonly audioFileInput = viewChild.required<ElementRef<HTMLInputElement>>("audioFileInput");
    readonly audioPath = signal(this.data.submission.responseAudioPath);
    readonly audioPreviewUrl = signal<string | null>(null);
    readonly audioUploading = signal(false);
    // False only while audioPreviewUrl() is still the caller-provided data.audioPreviewUrl - that
    // one isn't ours to revoke. Any url this modal sets afterward (its own fetch, an upload, or
    // null) is ours, so this flips true the first time setAudioPreviewUrl() runs, permanently.
    private ownsAudioPreviewUrl = false;

    readonly selectedBadge = toSignal(this.badgeControl.valueChanges.pipe(map(id => this.findBadge(id))), {
        initialValue: this.findBadge(this.badgeControl.value)
    });

    private readonly textValue = toSignal(this.textControl.valueChanges, { initialValue: this.textControl.value });
    private readonly isOverridden = computed(() => this.textValue() !== (this.selectedBadge()?.response ?? ""));

    readonly showRemoveImage = computed(() => !!this.selectedBadge()?.imageUrl && !this.hideImage());
    readonly showResetToTemplate = computed(() => !!this.selectedBadge() && (this.hideImage() || this.isOverridden()));

    constructor() {
        this.badgeControl.valueChanges.subscribe(() => this.applyTemplate());

        if (this.data.audioPreviewUrl) {
            // don't have to re-fetch audio file
            this.audioPreviewUrl.set(this.data.audioPreviewUrl);
        } else if (this.data.submission.responseAudioPath) {
            this.submissionFreeformApi.getResponseAudioFile(this.data.submission.id).subscribe({
                next: audioFile => this.setAudioPreviewUrl(audioFile.url),
                error: () => this.snackbar.show("We couldn't load the existing response audio file.", "error")
            });
        }

        inject(DestroyRef).onDestroy(() => this.setAudioPreviewUrl(null));
    }

    private findBadge(id: string | null): BadgeResponse | undefined {
        return this.data.badges.find(badge => badge.id === id);
    }

    private initialText(): string {
        const { submission } = this.data;
        if (submission.responseCustomText !== null) return submission.responseCustomText;
        return this.findBadge(submission.responseRating)?.response ?? "";
    }

    applyTemplate(): void {
        const badge = this.findBadge(this.badgeControl.value);
        this.hideImage.set(false);
        this.textControl.setValue(badge?.response ?? "");
    }

    removeImage(): void {
        this.hideImage.set(true);
    }

    openAudioFileInput(): void {
        this.audioFileInput().nativeElement.click();
    }

    onAudioFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        // Reset the input so re-selecting the same file still fires a `change` event.
        input.value = "";
        if (file) this.uploadAudio(file);
    }

    removeAudio(): void {
        this.audioPath.set(null);
        this.setAudioPreviewUrl(null);
        this.audioChanged.set(this.data.submission.responseAudioPath !== null);
    }

    private uploadAudio(file: File): void {
        if (!ALLOWED_RESPONSE_AUDIO_FORMATS.some(format => format.mimeType === file.type)) {
            this.snackbar.show(`Unsupported file type: ${file.type}`, "error");
            return;
        }
        if (file.size > MAX_AUDIO_SIZE_BYTES) {
            this.snackbar.show(`File is too large: ${file.size} Bytes`, "error");
            return;
        }

        this.audioUploading.set(true);

        this.submissionFreeformApi.uploadResponseAudio(this.data.submission.id, file).subscribe({
            next: response => {
                this.audioPath.set(response.objectKey);
                this.setAudioPreviewUrl(URL.createObjectURL(file));
                this.audioUploading.set(false);
                this.audioChanged.set(true);
            },
            error: () => {
                this.snackbar.show("We couldn't upload the audio file. Please try again.", "error");
                this.audioUploading.set(false);
            }
        });
    }

    private setAudioPreviewUrl(url: string | null): void {
        const previous = this.audioPreviewUrl();
        if (previous && this.ownsAudioPreviewUrl) URL.revokeObjectURL(previous);
        this.audioPreviewUrl.set(url);
        this.ownsAudioPreviewUrl = true;
    }

    canSubmit(): boolean {
        return (
            !this.textControl.invalid &&
            !this.isSubmitting() &&
            !this.audioUploading() &&
            // A response needs some content to be submittable: text, a badge (the badge itself is
            // the content), or a link.
            (this.textControl.value.trim() !== "" ||
                this.badgeControl.value !== null ||
                this.linkControl.value.trim() !== "")
        );
    }

    submit(): void {
        if (!this.canSubmit()) return;

        const templateText = this.findBadge(this.badgeControl.value)?.response ?? "";
        const text = this.textControl.value;

        const request: SubmissionFreeformHostResponseRequest = {
            responseRating: this.badgeControl.value,
            responseCustomText: this.textControl.value === templateText ? null : text,
            responseHideBadgeImage: this.hideImage(),
            responseLink: this.linkControl.value.trim() || null,
            responseAudioPath: this.audioPath()
        };

        this.isSubmitting.set(true);
        this.badgeControl.disable();
        this.textControl.disable();
        this.linkControl.disable();
        this.dialogRef.disableClose = true;

        this.submissionFreeformApi.updateResponse(this.data.submission.id, request).subscribe({
            next: () => {
                this.dialogRef.close({ audioChanged: this.audioChanged() });
            },
            error: (error: HttpErrorResponse) => {
                this.isSubmitting.set(false);
                this.badgeControl.enable();
                this.textControl.enable();
                this.linkControl.enable();
                this.dialogRef.disableClose = false;
                this.snackbar.show(
                    error.error?.detail ?? `We couldn't ${this.data.isEditing ? "update" : "post"} the response.`,
                    "error"
                );
            }
        });
    }

    close(): void {
        if (this.isSubmitting()) return;
        this.dialogRef.close();
    }

    protected readonly ButtonVariant = ButtonVariant;
    protected readonly responseTextMaxLength = RESPONSE_TEXT_MAX_LENGTH;
    protected readonly audioAcceptAttr = ALLOWED_RESPONSE_AUDIO_FORMATS.flatMap(format => [
        format.mimeType,
        `.${AUDIO_FILE_EXTENSIONS[format.mimeType]}`
    ]).join(",");
    protected readonly audioFormatLabel = ALLOWED_RESPONSE_AUDIO_FORMATS.map(format => format.label).join(" or ");
    protected readonly maxAudioSizeMb = MAX_AUDIO_SIZE_MB;
}
